import React, { CSSProperties, useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import classNames from "classnames";
import Masthead from "components/Masthead";
import MainContent from "components/MainContent";
import AppSideNav from "components/SideNav/AppSideNav";
import { GameNav } from "@ugitgud/legos/components/nav/GameNav/GameNav";
import { AppRoutes } from "@ugg/shared/routes/app-routes";
import { useGlobalBanner } from "@ugitgud/legos/components/banner/BannerContainer";
import { CurrentBanner } from "@ugitgud/legos/components/banner/BannerContainer";
import { CURRENT_ACTIVE_BANNER } from "@ugitgud/legos/hooks/useBanner";

// For login and signup pages we don't want to display sideNav or Masthead
function isAccountPage(pathname: string) {
  // Use AppRoutes constants for all account page prefixes
  return (
    pathname.startsWith(AppRoutes.AUTH) ||
    pathname.startsWith(AppRoutes.LOGIN) ||
    pathname.startsWith(AppRoutes.SIGNUP) ||
    pathname.startsWith(AppRoutes.RECOVER_ACCOUNT) ||
    pathname.startsWith(AppRoutes.RESET_PASSWORD) ||
    pathname.startsWith(AppRoutes.VERIFICATION) ||
    pathname.startsWith(AppRoutes.VERIFY)
  );
}

function isPremiumPage(pathname: string) {
  return pathname.match(/^\/boost\//);
}

const AppLayout = () => {
  const location = useLocation();
  const history = useHistory();
  const [sideNavOpen, setSideNavOpen] = useState(false);
  const { openBannerIds } = useGlobalBanner();
  const isBannerOpen = openBannerIds.includes(CURRENT_ACTIVE_BANNER);
  const isLanding = location.pathname === AppRoutes.HOME;

  useEffect(() => {
    if (location.pathname !== location.pathname.toLowerCase()) {
      history.replace(location.pathname.toLowerCase() + location.search);
    }
  }, [location.pathname]);

  // For account pages, remove masthead height and margin
  const mastheadHeightVarValue = isAccountPage(location.pathname)
    ? "0px"
    : `calc(65px + var(--game-nav-height) + ${isBannerOpen ? "48px" : "0px"})`;
  const mastheadHeightVar = {
    "--masthead-height": mastheadHeightVarValue,
  } as unknown as CSSProperties;
  const topPositionStyles: CSSProperties =
    isBannerOpen && !isAccountPage(location.pathname)
      ? { top: `calc(var(--game-nav-height) + var(--banner-height))` }
      : { top: `var(--game-nav-height)` };

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--masthead-height", mastheadHeightVarValue);
  }, [mastheadHeightVarValue, isBannerOpen]);

  return (
    <div
      className={classNames(
        "max-w-[100%]",
        "router-container", // DO NOT REMOVE 'router-container' class. REQUIRED FOR ADS!!!!
      )}
      style={mastheadHeightVar}
    >
      {/* Only show Masthead/GameNav/Banner for non-account pages */}
      {!isAccountPage(location.pathname) && (
        <div className="fixed top-0 left-0 w-full z-[999] transition-[height]">
          <CurrentBanner url="/plus" />
          <GameNav selectedGame="lol" />
          <Masthead sideNavOpen={sideNavOpen} setSideNavOpen={setSideNavOpen} />
        </div>
      )}
      <div
        id="page-content"
        className={classNames("flex transition-[margin-top]", {
          // Remove margin for account pages
          "mt-[var(--masthead-height)] sm:mt-[var(--game-nav-height)]": isLanding && !isAccountPage(location.pathname),
          "mt-[var(--masthead-height)]": !isLanding && !isAccountPage(location.pathname),
        })}
      >
        {/* DO NOT REMOVE 'page-content' id. REQUIRED FOR ADS!!!!  */}
        {/* Only show SideNav for non-account and non-premium pages */}
        {!isAccountPage(location.pathname) && !isPremiumPage(location.pathname) && (
          <AppSideNav open={sideNavOpen} setOpen={setSideNavOpen} style={topPositionStyles} />
        )}
        <div className="w-full">
          {/* DO NOT REMOVE DIV. REQUIRED FOR ADS!!!!  */}
          <MainContent isAccountPage={isAccountPage(location.pathname)} />
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
